import type { ISidebarMenuItem, IUserProfile } from '../../../interfaces'

/**
 * Declarative Arc sidebar menus (AR-578). These are the exact items the old
 * imperative transformMenuForArcProjects produced from the legacy menus,
 * written down per profile so order and links are explicit and testable.
 * Pure module: no hooks, no side effects.
 */

export interface IArcSidebarMenuItem extends ISidebarMenuItem {
  /**
   * True when the link is a route the SPA router owns: the sidebar navigates
   * client-side and the persistent Arc shell (top bar + sidebar) survives.
   * False for monolith (Rails) pages, which need a full document load.
   */
  spaRoute: boolean
}

const ARC_PROJECTS_LINK = '/arc_projects'

export const getArcStudentMenu = (userID?: string): IArcSidebarMenuItem[] => [
  { link: '/student_room/dashboard', key: 'dashboard', icon: 'icon-home', spaRoute: true },
  { link: ARC_PROJECTS_LINK, key: 'projects', icon: 'icon-activities', spaRoute: true },
  // AR-898: the student's own Tasks Tracker, same key/icon as the teacher's entry.
  { link: '/app/student-task-tracker', key: 'task_tracker', icon: 'icon-tasks-tracker', spaRoute: true },
  {
    link: userID ? `/students/${userID}/student_mosaics` : null,
    key: 'student_mosaics',
    icon: 'icon-mosaic',
    spaRoute: false,
  },
  { link: '/school_term_assessments', key: 'school_term_assessments', icon: 'icon-reports', spaRoute: false },
]

const buildTutorManagerMenu = (dashboardLink: string, dashboardSpaRoute: boolean): IArcSidebarMenuItem[] => [
  { link: dashboardLink, key: 'dashboard', icon: 'icon-home', spaRoute: dashboardSpaRoute },
  { link: '/teacher/settings', key: 'settings', icon: 'icon-md-settings', spaRoute: true },
  { link: '/teacher/manage-students', key: 'students', icon: 'icon-students', spaRoute: true },
  { link: ARC_PROJECTS_LINK, key: 'projects', icon: 'icon-activities', spaRoute: true },
  { link: '/app/task-tracker', key: 'task_tracker', icon: 'icon-tasks-tracker', spaRoute: true },
  { link: '/reports/dashboard', key: 'reports', icon: 'icon-students-report', spaRoute: true },
  { link: '/app/resource-center', key: 'resource_center', icon: 'icon-resource-center', spaRoute: true },
]

export const arcGuardianMenu: IArcSidebarMenuItem[] = [
  { link: '/guardian_room/dashboard', key: 'dashboard', icon: 'icon-home', spaRoute: true },
  { link: ARC_PROJECTS_LINK, key: 'projects', icon: 'icon-activities', spaRoute: true },
  { link: '/end_of_term_reports', key: 'end_of_term_reports', icon: 'icon-reports', spaRoute: false },
]

/**
 * Menu for a profile under the Arc flag.
 * @param managerDashboardEnabled Keeps the legacy manager dashboard link when
 * the Arc manager dashboard rollout is off (same fallback the old transform had).
 */
export const getArcMenu = (
  profile: IUserProfile | undefined,
  userID: string | undefined,
  managerDashboardEnabled: boolean,
): IArcSidebarMenuItem[] => {
  switch (profile) {
    case 'student':
      return getArcStudentMenu(userID)
    case 'tutor':
      return buildTutorManagerMenu('/teacher/dashboard', true)
    case 'manager':
      return managerDashboardEnabled
        ? buildTutorManagerMenu('/app/manager/dashboard', true)
        : buildTutorManagerMenu('/manager/dashboard', false)
    case 'guardian':
      return arcGuardianMenu
    default:
      return []
  }
}

/**
 * Active-route detection for Arc sidebar items (prototype behaviour): a link
 * is active on an exact match or when the pathname is nested under it.
 * The Arc projects entry also owns the project view/edit routes, which still
 * live under `/projects/:id` (while `/projects_library` and friends do not
 * match, since nesting requires the `/` boundary).
 */
export const isMenuItemActive = (link: string | null, pathname: string): boolean => {
  if (!link) return false
  if (link === '/') return pathname === '/'
  if (pathname === link || pathname.startsWith(`${link}/`)) return true
  if (link === ARC_PROJECTS_LINK) {
    return pathname === '/projects' || pathname.startsWith('/projects/')
  }
  return false
}
